/**
 * Utility functions for transforming backend duplicate detection responses
 * to match frontend component expectations.
 */

import { logWarn } from './logger';

// Base invoice data structure
export interface InvoiceData {
  supplier: string;
  invoice_number: string;
  invoice_date: string;
  total_amount: string;
  supplier_abn: string;
  page_count: number;
  start_page: number;
}

// Processing summary structure
export interface ProcessingSummary {
  totalProcessed: number;
  successfullyCreated: number;
  skippedAsDuplicates: number;
  createdButFlaggedAsDuplicates: number;
  message: string;
}

// Valid match types for duplicate detection
export type ValidMatchType = 'supplier_name' | 'supplier_abn';

// Duplicate match details from backend
export interface BackendDuplicateMatchDetails {
  matchType: ValidMatchType;
  originalInvoiceId: string;
  originalCreatedDate: string;
}

// Skipped duplicate structure from backend
export interface BackendSkippedDuplicate {
  invoiceIndex: number;
  supplier: string;
  invoiceNumber: string;
  rejectionReason: string;
  duplicateDetails?: BackendDuplicateMatchDetails;
}

// Duplicate invoice structure from backend
export interface BackendDuplicateInvoice {
  invoiceIndex: number;
  invoiceId?: string;
  supplier: string;
  invoiceNumber: string;
  rejectionReason?: string;
  duplicateDetails?: BackendDuplicateMatchDetails;
}

// Complete backend response structure
export interface BackendUploadResponse {
  success: boolean;
  data: any[]; // Created invoice records
  originalData: InvoiceData | InvoiceData[];
  originalFilename?: string;
  s3Keys?: string[];
  individualPdfFilenames?: string[];
  duplicatesDetected: boolean;
  duplicateCount: number;
  skippedDuplicates?: BackendSkippedDuplicate[];
  duplicateInvoices?: BackendDuplicateInvoice[];
  processingSummary: ProcessingSummary;
}

// Extended interface for responses that include duplicate detection
export interface BackendDuplicateResponse extends BackendUploadResponse {
  duplicatesDetected: true;
  duplicateCount: number;
  skippedDuplicates?: BackendSkippedDuplicate[];
  duplicateInvoices?: BackendDuplicateInvoice[];
}

/**
 * Type guard to validate match type values
 * @param value - The value to validate
 * @returns True if the value is a valid match type
 */
export function isValidMatchType(value: unknown): value is ValidMatchType {
  return value === 'supplier_name' || value === 'supplier_abn';
}

/**
 * Safely extracts and validates match type from duplicate details
 * @param duplicateDetails - The duplicate details object
 * @returns Validated match type or undefined if invalid
 */
export function getValidatedMatchType(
  duplicateDetails: BackendDuplicateMatchDetails | undefined
): ValidMatchType | undefined {
  if (!duplicateDetails?.matchType) {
    return undefined;
  }
  
  if (isValidMatchType(duplicateDetails.matchType)) {
    return duplicateDetails.matchType;
  }
  
  // Log warning for invalid match type
  logWarn(
    `Invalid match type received from backend: "${duplicateDetails.matchType}". ` +
    `Expected one of: "supplier_name", "supplier_abn". ` +
    `Using fallback value.`,
    'DuplicateResponseTransformer',
    { invalidMatchType: duplicateDetails.matchType, expectedTypes: ['supplier_name', 'supplier_abn'] }
  );
  
  return undefined;
}

export interface TransformedDuplicateDetails {
  summary: {
    totalDuplicatesFound: number;
    duplicatesSkipped: number;
    duplicatesCreatedAnyway: number;
    allowDuplicatesWasEnabled: boolean;
  };
  skippedInvoices: Array<{
    invoiceIndex: number;
    supplier: string;
    invoiceNumber: string;
    reason: string;
    originalInvoice?: {
      id: string;
      invoiceNumber: string;
      createdDate: string;
      matchType: ValidMatchType;
      matchTypeKey: ValidMatchType;
    };
  }>;
  createdDuplicates: Array<{
    invoiceIndex: number;
    invoiceId: string;
    supplier: string;
    invoiceNumber: string;
    warning: string;
    originalInvoice?: {
      id: string;
      invoiceNumber: string;
      createdDate: string;
      matchType: ValidMatchType;
      matchTypeKey: ValidMatchType;
    };
  }>;
  recommendations: {
    message: string;
    actions: string[];
  };
}

/**
 * Transforms backend duplicate detection response to match frontend component expectations.
 * 
 * @param response - The backend response containing duplicate detection information
 * @returns Transformed response with duplicateDetails in the expected format
 */
export function transformBackendDuplicateResponse(
  response: BackendUploadResponse
): BackendUploadResponse & { duplicateDetails?: TransformedDuplicateDetails } {
  return {
    ...response,
    duplicateDetails: response.duplicatesDetected ? {
      summary: {
        totalDuplicatesFound: response.duplicateCount || 0,
        duplicatesSkipped: response.skippedDuplicates?.length || 0,
        duplicatesCreatedAnyway: response.duplicateInvoices?.length || 0,
        allowDuplicatesWasEnabled: false // Backend doesn't provide this info
      },
      skippedInvoices: response.skippedDuplicates?.map((duplicate) => ({
        invoiceIndex: duplicate.invoiceIndex,
        supplier: duplicate.supplier,
        invoiceNumber: duplicate.invoiceNumber,
        reason: duplicate.rejectionReason,
        originalInvoice: duplicate.duplicateDetails ? {
          id: duplicate.duplicateDetails.originalInvoiceId,
          invoiceNumber: duplicate.invoiceNumber,
          createdDate: duplicate.duplicateDetails.originalCreatedDate,
          matchType: duplicate.duplicateDetails.matchType,
          matchTypeKey: getValidatedMatchType(duplicate.duplicateDetails) || 'supplier_name'
        } : undefined
      })) || [],
      createdDuplicates: response.duplicateInvoices?.map((duplicate) => ({
        invoiceIndex: duplicate.invoiceIndex,
        invoiceId: duplicate.invoiceId || duplicate.invoiceIndex.toString(),
        supplier: duplicate.supplier,
        invoiceNumber: duplicate.invoiceNumber,
        warning: duplicate.rejectionReason || 'Duplicate invoice detected',
        originalInvoice: duplicate.duplicateDetails ? {
          id: duplicate.duplicateDetails.originalInvoiceId,
          invoiceNumber: duplicate.invoiceNumber,
          createdDate: duplicate.duplicateDetails.originalCreatedDate,
          matchType: duplicate.duplicateDetails.matchType,
          matchTypeKey: getValidatedMatchType(duplicate.duplicateDetails) || 'supplier_name'
        } : undefined
      })) || [],
      recommendations: {
        message: `Found ${response.duplicateCount} duplicate invoices. Review the details below to understand why they were flagged as duplicates.`,
        actions: [
          'Check if these are truly duplicate invoices',
          'Verify supplier information matches existing records',
          'Consider updating supplier details if needed',
          'Contact support if you believe this is an error'
        ]
      }
    } : undefined
  };
}
